import './PureCloudLogin.css'
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '../../../config';
import {
    AUTH_LOG_PREFIX,
    AuthenticationConstants,
    clearAuthSession,
    getCloudEnv,
    getOAuthClientId,
    isStorageAvailable,
    isTokenValid,
    openAuthPopup,
    setCloudEnv,
    setPostLoginPath,
    shouldUsePopupAuth
} from '../../utilities/authConstants';

/*
 * SDK is loaded via the CDN <script> tag in public/index.html and self-
 * registers under `window.require('platformClient')` (Browserify pattern).
 * Importing the npm package directly does NOT work under CRA / webpack 5:
 * the package's `browser` field points to a UMD bundle that webpack cannot
 * unwrap, so `platformClient.ApiClient` ends up undefined at runtime.
 */
const getPlatformClient = () => {
    // window.require('platformClient') THROWS (browserify "Cannot find module")
    // when the CDN SDK script has not registered the module yet, so this must
    // be wrapped by callers.
    try {
        return window.require && window.require('platformClient');
    } catch (err) {
        return null;
    }
};

/*
 * The Genesys SDK is a third-party CDN <script> (see public/index.html), not
 * a bundled dependency. On a cold load it can lag behind the React app, and
 * a direct `window.require('platformClient')` either throws or returns
 * undefined. Poll until the SDK is usable (or time out) before starting PKCE
 * so the redirect is deterministic.
 */
const waitForPlatformClient = (timeoutMs = 8000, intervalMs = 100) => {
    return new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
            const client = getPlatformClient();
            if (client && client.ApiClient) {
                resolve(client);
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                resolve(null);
                return;
            }
            setTimeout(check, intervalMs);
        };
        check();
    });
};

const PureCloudLogin = () => {
    const navigate = useNavigate();
    const startedRef = useRef(false);
    const [statusMsg, setStatusMsg] = useState('Redirecting to Genesys Cloud...');
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        // Guard against React 18 StrictMode double-invoke.
        if (startedRef.current) return;
        startedRef.current = true;

        const run = async () => {
            // Storage must be usable.
            if (!isStorageAvailable()) {
                console.error(AUTH_LOG_PREFIX, 'sessionStorage unavailable');
                navigate('/unauthorized', {
                    state: { error: 'Browser storage is required for sign-in. Please disable private mode and retry.' }
                });
                return;
            }

            // Resolve env: query param > sessionStorage > config default.
            let env = new URLSearchParams(window.location.search).get('environment');
            if (!env) {
                env = getCloudEnv() || (config && config.defaultEnvironment) || null;
            }
            if (!env) {
                console.error(AUTH_LOG_PREFIX, 'Could not resolve Genesys environment');
                navigate('/unauthorized', { state: { error: 'Unable to determine Genesys Cloud environment.' } });
                return;
            }
            setCloudEnv(env);

            // Short-circuit: if we already hold a valid token, skip PKCE.
            if (isTokenValid()) {
                console.debug(AUTH_LOG_PREFIX, 'PureCloudLogin: token already valid, skipping login');
                navigate('/');
                return;
            }

            const clientId = getOAuthClientId();
            if (!clientId) {
                console.error(AUTH_LOG_PREFIX, 'OAuth clientId missing for env', env);
                navigate('/unauthorized', {
                    state: { error: `Missing OAuth client for environment ${env}` }
                });
                return;
            }

            // Wait for the CDN-loaded SDK to be ready instead of assuming it.
            setStatusMsg('Loading sign-in library...');
            const platformClient = await waitForPlatformClient();
            if (!platformClient || !platformClient.ApiClient) {
                console.error(AUTH_LOG_PREFIX, 'Genesys SDK not loaded after wait; CDN script missing/blocked?');
                setError({
                    kind: 'retry',
                    message: 'The sign-in library failed to load. This is usually a temporary network issue.'
                });
                return;
            }
            setStatusMsg('Redirecting to Genesys Cloud...');
            const client = platformClient.ApiClient.instance;

            try {
                client.setEnvironment(env);
            } catch (envErr) {
                console.error(AUTH_LOG_PREFIX, 'Invalid Genesys environment', envErr);
                navigate('/unauthorized', { state: { error: `Invalid environment "${env}"` } });
                return;
            }

            // SDK safety: refuse to continue on an SDK that does not expose
            // PKCE methods (would only happen if the CDN URL was pinned to
            // a pre-v145 release).
            if (typeof client.loginPKCEGrant !== 'function') {
                console.error(AUTH_LOG_PREFIX, 'loginPKCEGrant unavailable in installed SDK');
                navigate('/unauthorized', { state: { error: 'OAuth library is out of date.' } });
                return;
            }

            // Preserve any pre-existing post-login path (set by a guard like
            // PrivateRoute before bouncing here); never trample.
            let existingPostLogin = null;
            try {
                existingPostLogin = sessionStorage.getItem(AuthenticationConstants.PostLoginPath);
            } catch (e) { /* noop */ }
            if (!existingPostLogin) {
                setPostLoginPath('/');
            }

            const usePopup = shouldUsePopupAuth();
            if (usePopup) {
                setStatusMsg('Opening sign-in window...');
                console.debug(AUTH_LOG_PREFIX, 'PureCloudLogin: using popup auth');
                const result = await openAuthPopup();
                if (result && result.accessToken) {
                    try {
                        sessionStorage.setItem(AuthenticationConstants.CloudCSPToken, result.accessToken);
                        if (Number.isFinite(result.expireOn)) {
                            sessionStorage.setItem(AuthenticationConstants.ExpireOn, String(result.expireOn));
                        }
                    } catch (storageErr) {
                        console.error(AUTH_LOG_PREFIX, 'Failed to persist token from popup', storageErr);
                        navigate('/unauthorized', { state: { error: 'Browser storage is unavailable.' } });
                        return;
                    }
                    navigate('/');
                    return;
                }
                if (result && result.blocked) {
                    console.warn(AUTH_LOG_PREFIX, 'Popup blocked; falling back to in-frame redirect');
                    setStatusMsg('Popup was blocked. Redirecting in this window...');
                } else if (result && result.cancelled) {
                    setStatusMsg('Sign-in was cancelled.');
                    return;
                } else if (result && result.error) {
                    console.error(AUTH_LOG_PREFIX, 'Popup auth error', result.error);
                    setStatusMsg('Sign-in failed.');
                    return;
                }
                // Fall through to in-frame redirect on popup blocked.
            }

            try {
                console.debug(AUTH_LOG_PREFIX, 'PureCloudLogin: starting loginPKCEGrant');
                const redirectUri = `${window.location.protocol}//${window.location.host}/oauth/callback`;
                // Redirect watchdog: a successful loginPKCEGrant navigates the
                // whole page away, which tears down this timer. If it is still
                // running after the deadline, the redirect never happened
                // (SDK resolved without navigating, or an in-frame redirect was
                // blocked by X-Frame-Options) -> recover instead of hanging on
                // "Redirecting to Genesys Cloud...".
                const redirectWatchdog = setTimeout(() => {
                    console.error(AUTH_LOG_PREFIX, 'loginPKCEGrant did not redirect within deadline');
                    setError({
                        kind: 'retry',
                        message: 'Sign-in did not start. This can happen on a slow network or when the app is embedded in a frame.'
                    });
                }, 10000);
                const authData = await client.loginPKCEGrant(clientId, redirectUri);
                // Reaching here means the SDK returned WITHOUT navigating away.
                // That is a valid success path: loginPKCEGrant only performs
                // an interactive redirect when a fresh login is required; when
                // it already holds (or silently obtained) a valid token it
                // simply resolves with the auth data. Treat a token-bearing
                // resolve as success and continue.
                clearTimeout(redirectWatchdog);
                const accessToken =
                    (authData && authData.accessToken) ||
                    (client.authData && client.authData.accessToken) ||
                    null;
                if (accessToken) {
                    try {
                        sessionStorage.setItem(AuthenticationConstants.CloudCSPToken, accessToken);
                        const expiryRaw =
                            (authData && authData.tokenExpiryTime) ||
                            (client.authData && client.authData.tokenExpiryTime);
                        if (expiryRaw !== undefined && expiryRaw !== null) {
                            const expireOn = typeof expiryRaw === 'number'
                                ? expiryRaw
                                : Date.parse(String(expiryRaw));
                            if (Number.isFinite(expireOn)) {
                                sessionStorage.setItem(AuthenticationConstants.ExpireOn, String(expireOn));
                            }
                        }
                    } catch (storageErr) {
                        console.error(AUTH_LOG_PREFIX, 'Failed to persist token from loginPKCEGrant', storageErr);
                        navigate('/unauthorized', { state: { error: 'Browser storage is unavailable.' } });
                        return;
                    }
                    console.debug(AUTH_LOG_PREFIX, 'loginPKCEGrant resolved with token; navigating home');
                    navigate('/');
                    return;
                }
                // No redirect AND no token: genuinely could not start sign-in.
                console.error(AUTH_LOG_PREFIX, 'loginPKCEGrant resolved without token or redirect');
                setError({
                    kind: 'retry',
                    message: 'Sign-in did not start as expected. Please retry.'
                });
            } catch (err) {
                // Any synchronous/early throw from the SDK.
                console.error(AUTH_LOG_PREFIX, 'loginPKCEGrant threw before redirect', err);
                clearAuthSession();
                navigate('/unauthorized', { state: { error: 'Sign-in could not be started.' } });
            }
        };

        // Never let the async flow reject silently and hang the page on the
        // status message. Surface a retry instead.
        run().catch((err) => {
            console.error(AUTH_LOG_PREFIX, 'PureCloudLogin fatal', err);
            setError({ kind: 'fatal', message: 'An unexpected error occurred while starting sign-in.' });
        });
        // retryCount in deps lets the user re-trigger the flow after a failure.
        // eslint-disable-next-line
    }, [navigate, retryCount]);

    const handleRetry = () => {
        setError(null);
        setStatusMsg('Redirecting to Genesys Cloud...');
        startedRef.current = false;
        setRetryCount((c) => c + 1);
    };

    if (error) {
        return (
            <Fragment>
                <div style={{ textAlign: 'center' }}>
                    <p>{error.message}</p>
                    <button type="button" onClick={handleRetry}>Retry sign-in</button>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <div style={{ textAlign: 'center' }}>
                {statusMsg}
            </div>
        </Fragment>
    );
};

export default PureCloudLogin;
